import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import gql from 'graphql-tag';
import React from 'react';

import * as ArchiveFilterUtil from 'archive/ArchiveFilterUtil';
import {assertAndContinue} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import {useBenchlingQuery, QueryResultType} from 'graphql-client/BenchlingQuery';
import {trackOpenedRelevantTemplatesAdminHelp} from 'nav/analytics/RelevantTemplatesAnalytics';
import notifications from 'notifications';
import {Organization} from 'orgs/OrganizationConstants';
import BenchlingProfiler, {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  RELEVANT_TEMPLATES_SEARCH_STARTED_EVENT,
  RELEVANT_TEMPLATES_SEARCH_RESULTS_RENDERED_EVENT,
} from 'performance/processNavigationEvents';
import EntityStore from 'stores/EntityStore';
import InformationalBanner from 'ui-library/banners/InformationalBanner';
import {GRAY_DARKER} from 'ui-library/Colors';
import {useDebounce} from 'ui-library/hooks/useDebounce';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import ResultsPager from 'ui-library/ResultsPager';
import APIUtil from 'util/APIUtil';

import {
  RelevantTemplatesTableQuery,
  RelevantTemplatesTableQueryVariables,
} from './__generated/RelevantTemplatesTableQuery';
import {
  NO_RELEVANT_TEMPLATES_STRING,
  RELEVANT_TEMPLATES_SEARCH_DEBOUNCE_MS,
} from './RelevantTemplatesConstants';
import {RelevantTemplatesHeader} from './RelevantTemplatesHeader';
import RelevantTemplatesTable, {RELEVANT_TEMPLATES_TABLE_FRAGMENT} from './RelevantTemplatesTable';
import {useRelevantTemplatesListing} from './useRelevantTemplatesListing';

type RelevantTemplateNode =
  RelevantTemplatesTableQuery['relevantTemplatesListing']['relevantTemplates'][number];

export const RELEVANT_TEMPLATES_TABLE_QUERY = gql`
  query RelevantTemplatesTableQuery(
    $searchInput: RelevantTemplatesListingSearchInput!
    $limitOffsetInput: LimitOffsetInput!
  ) @skipAudit @engComponent(name: SCHEMAS) {
    relevantTemplatesListing(searchInput: $searchInput, limitOffsetInput: $limitOffsetInput) {
      totalInaccessible
      totalPostFiltering
      accessibleCollections {
        id
        name
      }
      ...RelevantTemplatesTableFragment
    }
  }
  ${RELEVANT_TEMPLATES_TABLE_FRAGMENT}
`;

export function inaccessibleTemplatesString(totalInaccessible: number): string {
  const verb = pluralize(totalInaccessible, 'is', 'are');
  const templateString = pluralize(totalInaccessible, 'template');
  return `There ${verb} ${totalInaccessible} ${templateString} that you do not have access to.`;
}

interface InaccessibleTemplatesDisplayProps {
  schemaId: string;
  schemaOrgUrlId: string;
  schemaType: string;
  totalInaccessible: number;
}

/**
 * InformationalBanner to show the number of inaccessible templates and direct users to their organization
 * admins to resolve any permissions issues.
 * @package
 */
export function InaccessibleTemplatesDisplay({
  schemaId,
  schemaOrgUrlId,
  schemaType,
  totalInaccessible,
}: InaccessibleTemplatesDisplayProps): JSX.Element | null {
  assertAndContinue(totalInaccessible > 0, 'totalInaccessible should be greater than 0');
  const organizationUrl = `/organizations/${schemaOrgUrlId}/members`;
  return (
    <InformationalBanner>
      {inaccessibleTemplatesString(totalInaccessible)} Reach out to an admin listed{' '}
      <a
        href={organizationUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => {
          trackOpenedRelevantTemplatesAdminHelp({
            schemaId,
            schemaType,
            isAdmin: EntityStore.currentUser().organizations.some((org: Organization) => org.admin),
            totalInaccessible,
          });
        }}
      >
        {' '}
        here
      </a>{' '}
      for support.
    </InformationalBanner>
  );
}

/**
 * Display component instead of table for when there are no relevant templates, or only inaccessible ones.
 * @package
 */
function NoRelevantTemplatesDisplay(props: {totalInaccessible: number}): JSX.Element {
  const {totalInaccessible} = props;
  const noRelevantTemplatesMessage =
    totalInaccessible > 0 ? inaccessibleTemplatesString(totalInaccessible) : NO_RELEVANT_TEMPLATES_STRING;
  return (
    <div className={classNames(css(styles.noRelevantTemplatesDisplay))}>
      <p>{noRelevantTemplatesMessage}</p>
    </div>
  );
}

export interface RelevantTemplatesTabProps {
  schemaId: string;
  schemaOrgUrlId: string;
}

/**
 * The Relevant Templates feature has only been tested and is supported for Entity (TagSchema) and Result (AssaySchema) schemas.
 */
export default function RelevantTemplatesTab({
  schemaId,
  schemaOrgUrlId,
}: RelevantTemplatesTabProps): JSX.Element | null {
  const [relevantTemplatesListingState, relevantTemplatesListingActions] = useRelevantTemplatesListing();
  const {listingCriteria, limitOffsetInput, selectedArchivePurposeFilters} = relevantTemplatesListingState;
  const {onChangeListingCriteria, onChangeOffset, onChangeSelectedArchivePurposeFilters} =
    relevantTemplatesListingActions;
  const debouncedListingCriteria = useDebounce(listingCriteria, RELEVANT_TEMPLATES_SEARCH_DEBOUNCE_MS);

  const filterForArchivePurpose = (templates: Array<RelevantTemplateNode>): Array<RelevantTemplateNode> => {
    const filteredTemplates =
      ArchiveFilterUtil.filterItemsToPurposes(
        templates.map((t) => t.template),
        selectedArchivePurposeFilters
      ) || [];
    return templates.filter((t) => filteredTemplates.includes(t.template));
  };

  const schemaType = APIUtil.apiIdToType(schemaId);
  assertAndContinue(
    APIUtil.isTagSchema(schemaId) || APIUtil.isAssaySchema(schemaId),
    `Given schema of type [${schemaType}] has not been tested and is not supported.`
  );

  const result = useBenchlingQuery<RelevantTemplatesTableQuery, RelevantTemplatesTableQueryVariables>(
    RELEVANT_TEMPLATES_TABLE_QUERY,
    {
      variables: {
        searchInput: {
          schemaId,
          ...debouncedListingCriteria,
        },
        limitOffsetInput,
      },
      fetchPolicy: 'cache-and-network',
      renderCachedDataWhenRefetching: true,
    }
  );

  if (result.type === QueryResultType.ERROR) {
    notifications.error('Could not load relevant templates.');
    return null;
  } else if (
    result.type === QueryResultType.PENDING ||
    (result.type === QueryResultType.SUCCESS && result.loading)
  ) {
    logProfilerTimingEvent(RELEVANT_TEMPLATES_SEARCH_STARTED_EVENT, {
      hasSearchTerm: Boolean(debouncedListingCriteria.searchTerm),
      hasCollectionFilter: debouncedListingCriteria.collectionIds.length !== 0,
    });
    // Continue to display cached data while we fetch in the background
    const accessibleCollections = result.data?.relevantTemplatesListing?.accessibleCollections;
    const totalInaccessible = result.data?.relevantTemplatesListing?.totalInaccessible;
    return (
      <div>
        <RelevantTemplatesHeader
          selectedArchivePurposeFilters={selectedArchivePurposeFilters}
          templateCollections={accessibleCollections ?? []}
          listingCriteria={listingCriteria}
          onChange={onChangeListingCriteria}
          onChangeSelectedArchivePurposeFilters={onChangeSelectedArchivePurposeFilters}
          schemaType={schemaType}
        />
        <CenteredHorizontalThrobber />
        {totalInaccessible != null && totalInaccessible > 0 && (
          <InaccessibleTemplatesDisplay
            schemaId={schemaId}
            schemaOrgUrlId={schemaOrgUrlId}
            schemaType={schemaType}
            totalInaccessible={totalInaccessible}
          />
        )}
      </div>
    );
  } else {
    const {relevantTemplates, totalPostFiltering, totalInaccessible, accessibleCollections} =
      result.data.relevantTemplatesListing;
    const noRelevantTemplates =
      listingCriteria.searchTerm === '' && !listingCriteria.collectionIds.length && totalPostFiltering === 0;
    const relevantTemplatesFiltered = filterForArchivePurpose(relevantTemplates);

    return (
      <div>
        <RelevantTemplatesHeader
          selectedArchivePurposeFilters={selectedArchivePurposeFilters}
          templateCollections={accessibleCollections}
          listingCriteria={listingCriteria}
          onChange={onChangeListingCriteria}
          onChangeSelectedArchivePurposeFilters={onChangeSelectedArchivePurposeFilters}
          schemaType={schemaType}
        />
        <BenchlingProfiler
          {...RELEVANT_TEMPLATES_SEARCH_RESULTS_RENDERED_EVENT}
          extra={{
            totalPostFiltering,
            totalInaccessible,
            totalAccessibleCollections: accessibleCollections.length,
          }}
        >
          {noRelevantTemplates ? (
            <NoRelevantTemplatesDisplay totalInaccessible={totalInaccessible} />
          ) : (
            <>
              <div className={classNames(css(styles.relevantTemplatesTable))}>
                <RelevantTemplatesTable
                  relevantTemplates={relevantTemplatesFiltered}
                  schemaType={schemaType}
                  schemaId={schemaId}
                />
                <ResultsPager
                  allowFastForwardAndBackward={true}
                  isLoading={false}
                  pagination={{...limitOffsetInput, total: totalPostFiltering}}
                  resultDescription="template"
                  setOffset={onChangeOffset}
                />
              </div>
              {totalInaccessible > 0 && (
                <InaccessibleTemplatesDisplay
                  schemaId={schemaId}
                  schemaOrgUrlId={schemaOrgUrlId}
                  schemaType={schemaType}
                  totalInaccessible={result.data.relevantTemplatesListing.totalInaccessible}
                />
              )}
            </>
          )}
        </BenchlingProfiler>
      </div>
    );
  }
}

const styles = StyleSheet.create({
  noRelevantTemplatesDisplay: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: GRAY_DARKER,
    height: 300,
  },
  relevantTemplatesTable: {
    marginTop: 10,
    marginBottom: 10,
  },
  helpButton: {
    marginLeft: 6,
  },
});
